import { authedFetch } from '../../lib/authedFetch';
import React, { useState, useRef, useEffect } from 'react';
import { formatInTenantTz } from '../../lib/datetime';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { useBusiness } from '../../contexts/BusinessContext';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { currentTenantRole } from '../../lib/role';
import { auth } from '../../lib/firebase';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Users,
  UserPlus,
  Edit,
  Trash2,
  Shield,
  UserCheck,
  Mail,
  Phone,
  Calendar,
  MoreVertical,
  AlertTriangle,
  Upload
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  canManageStaff,
  sanitizeCsvDisplay,
  isValidEmail,
  parseStaffCsv,
  dedupeByEmail,
  buildInvitePayload,
} from '../../lib/staffCsv';

const StaffManagement = () => {
  const { currentBusiness, getBusinessStaff, updateStaffRole, removeStaffMember } = useBusiness();
  const { toast } = useToast();
  const { t } = useLanguage();

  // Tier-based role allowances. Mirrors backend SUBSCRIPTION_TIERS.limits.
  const TIER_RBAC = {
    starter: {
      allowed: ['admin'],
      caps: { admin: 1 },
    },
    professional: {
      allowed: ['admin', 'manager', 'operator'],
      caps: { admin: 2, manager: 2, operator: 6 },
    },
    enterprise: {
      allowed: ['admin', 'manager', 'operator', 'analyst', 'it', 'director', 'executive'],
      caps: { admin: 5, manager: 5, operator: 100, analyst: 5, it: 5, director: 5, executive: 5 },
    },
  };
  const tierId = currentBusiness?.tier_id || 'professional';
  const tier = TIER_RBAC[tierId] || TIER_RBAC.professional;
  const isAllowedRole = (role) => tier.allowed.includes(role);
  
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [actionMenuStaff, setActionMenuStaff] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const actionMenuRef = useRef(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [staffToRemove, setStaffToRemove] = useState(null);
  // M24 — Change Role dialog (wires the action-menu item to the real role-change workflow)
  const [roleDialogStaff, setRoleDialogStaff] = useState(null);
  const [roleDialogValue, setRoleDialogValue] = useState('');
  const [roleDialogSaving, setRoleDialogSaving] = useState(false);
  const [inviteData, setInviteData] = useState({
    emails: '', // supports multiple emails (newline / comma / semicolon separated)
    customMessage: ''
  });
  const [parsedInvites, setParsedInvites] = useState([]);
  const [inviteSending, setInviteSending] = useState(false);
  // DR-011 CSV import + non-mutating server preview state.
  const [csvFileName, setCsvFileName] = useState('');
  const [csvRoleIgnored, setCsvRoleIgnored] = useState(false); // a CSV supplied a role column → show notice
  const [previewRows, setPreviewRows] = useState(null);        // server preview outcomes (row-level)
  const [previewing, setPreviewing] = useState(false);         // double-submit guard for preview
  const csvInputRef = useRef(null);

  // Parse email input for bulk invitations. OPERATOR-FIRST: no role is captured — every invitee onboards
  // as Operator; an Admin/Manager assigns the real role after onboarding.
  const parseEmailInput = (emailText) => {
    const emails = emailText
      .split(/[\n,;]+/)
      .map(email => email.trim())
      .filter(email => isValidEmail(email));

    return emails.map((email, index) => ({
      id: `invite_${index}`,
      email: email.toLowerCase(),
      name: email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      status: 'pending'
    }));
  };

  const handleBulkEmailChange = (value) => {
    setInviteData(prev => ({ ...prev, emails: value }));
    setPreviewRows(null); // any edit invalidates a stale preview
    if (value.trim()) {
      setParsedInvites(parseEmailInput(value));
    } else {
      setParsedInvites([]);
    }
  };

  // DR-011 CSV import: parse client-side, DEDUPE, merge emails into the shared invite list. The CSV role
  // column is NON-AUTHORITATIVE and IGNORED (everyone onboards as Operator) — we only flag that we saw one.
  const handleCsvFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseStaffCsv(text);
      const { unique } = dedupeByEmail(rows);
      const valid = unique.filter(r => isValidEmail(r.email));
      if (valid.length === 0) {
        toast({ title: t('ops.staff.csvNoValidRows'), description: t('ops.staff.csvNoValidRowsDesc'), variant: 'destructive' });
        return;
      }
      setCsvFileName(file.name);
      setCsvRoleIgnored(rows.some(r => r.rawRole)); // a role column was present → will be shown as ignored
      // Merge into the textarea + parsed list (dedupe against what's already staged).
      const existing = new Set(parsedInvites.map(p => p.email));
      const merged = [...parsedInvites];
      valid.forEach((r, i) => {
        if (!existing.has(r.email)) {
          merged.push({ id: `csv_${Date.now()}_${i}`, email: r.email, name: r.name, status: 'pending' });
          existing.add(r.email);
        }
      });
      setParsedInvites(merged);
      setInviteData(prev => ({ ...prev, emails: merged.map(m => m.email).join('\n') }));
      setPreviewRows(null);
    } catch (err) {
      toast({ title: t('common.error'), description: t('ops.staff.csvReadFailed'), variant: 'destructive' });
    } finally {
      if (csvInputRef.current) csvInputRef.current.value = ''; // allow re-selecting the same file
    }
  };

  // Non-mutating SERVER preview — classifies each row (eligible / already-staff / pending / duplicate /
  // invalid) WITHOUT creating any invitation. Tenant is derived from the authenticated business context.
  const handlePreviewInvites = async () => {
    if (previewing || parsedInvites.length === 0) return;
    setPreviewing(true);
    try {
      const res = await authedFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/staff/invite/preview`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildInvitePayload(currentBusiness?.brandId, parsedInvites)),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Preview failed');
      setPreviewRows(data.rows || []);
    } catch (error) {
      toast({ title: t('common.error'), description: error.message || t('ops.staff.previewFailed'), variant: 'destructive' });
    } finally {
      setPreviewing(false);
    }
  };

  const resetInviteDialog = () => {
    setInviteData({ emails: '', customMessage: '' });
    setParsedInvites([]);
    setPreviewRows(null);
    setCsvFileName('');
    setCsvRoleIgnored(false);
  };

  const handleInviteStaff = async () => {
    if (inviteSending) return; // double-submit guard
    if (parsedInvites.length === 0) {
      toast({
        title: t('ops.staff.invalidEmailFormat'),
        description: t('ops.staff.enterValidEmails'),
        variant: "destructive",
      });
      return;
    }

    setInviteSending(true);
    try {
      // OPERATOR-FIRST: role is omitted entirely — the server onboards every invitee as Operator.
      // Tenant (brand_id) is derived from the authenticated business context, not user-entered.
      const payload = buildInvitePayload(currentBusiness?.brandId, parsedInvites);
      const res = await authedFetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/staff/invite`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Invite request failed');
      }

      const sentCount = data.sent ?? 0;
      const totalCount = data.total ?? parsedInvites.length;
      const failed = (data.results || []).filter(r => !r.email_sent);

      if (sentCount > 0) {
        toast({
          title: t('ops.staff.invitationsSent'),
          description: `${t('ops.staff.invitesSentDesc', { sent: sentCount, total: totalCount })}${failed.length > 0 ? t('ops.staff.invitesFailedSuffix', { failed: failed.length }) : ''}`,
        });
      } else {
        toast({
          title: t('ops.staff.noInvitationsSent'),
          description: failed.map(f => `${f.email}: ${f.error}`).join(' | ') || t('ops.staff.allInvitesFailed'),
          variant: "destructive",
        });
      }

      resetInviteDialog();
      setInviteDialogOpen(false);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: error.message || t('ops.staff.failedToSendInvitations'),
        variant: "destructive",
      });
    } finally {
      setInviteSending(false);
    }
  };

  // Map a server preview outcome code → localized label + badge colour (safe, no raw ids/tokens).
  const previewOutcomeMeta = (outcome) => ({
    ELIGIBLE: { label: t('ops.staff.outcomeEligible'), cls: 'bg-green-100 text-green-800' },
    ALREADY_STAFF: { label: t('ops.staff.outcomeAlreadyStaff'), cls: 'bg-gray-100 text-gray-700' },
    INVITE_ALREADY_PENDING: { label: t('ops.staff.outcomePending'), cls: 'bg-amber-100 text-amber-800' },
    DUPLICATE_IN_BATCH: { label: t('ops.staff.outcomeDuplicate'), cls: 'bg-amber-100 text-amber-800' },
    INVALID_EMAIL: { label: t('ops.staff.outcomeInvalidEmail'), cls: 'bg-red-100 text-red-800' },
    INVALID_ROLE: { label: t('ops.staff.outcomeInvalidRole'), cls: 'bg-red-100 text-red-800' },
  }[outcome] || { label: outcome, cls: 'bg-gray-100 text-gray-700' });

  const roleDefinitions = {
    admin: {
      title: t('role.admin'),
      color: 'bg-purple-100 text-purple-800',
      description: t('ops.staff.descAdmin')
    },
    operator: {
      title: t('role.operator'),
      color: 'bg-blue-100 text-blue-800',
      description: t('ops.staff.descOperator')
    },
    manager: {
      title: t('role.manager'),
      color: 'bg-orange-100 text-orange-800',
      description: t('ops.staff.descManager')
    },
    analyst: {
      title: t('role.analyst'),
      color: 'bg-teal-100 text-teal-800',
      description: t('ops.staff.descAnalyst')
    },
    director: {
      title: t('role.director'),
      color: 'bg-indigo-100 text-indigo-800',
      description: t('ops.staff.descDirector')
    },
    executive: {
      title: t('role.executive'),
      color: 'bg-rose-100 text-rose-800',
      description: t('ops.staff.descExecutive')
    },
    it: {
      title: t('role.it'),
      color: 'bg-gray-100 text-gray-800',
      description: t('ops.staff.descIt')
    }
  };

  // Close action menu when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setActionMenuOpen(false);
        setActionMenuStaff(null);
      }
    };
    if (actionMenuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [actionMenuOpen]);

  const openActionMenu = (staff, e) => {
    e.stopPropagation();
    if (actionMenuOpen && actionMenuStaff?.id === staff.id) {
      setActionMenuOpen(false);
      setActionMenuStaff(null);
    } else {
      setActionMenuStaff(staff);
      setActionMenuOpen(true);
    }
  };

  const handleRoleUpdate = async (staffId, newRole) => {
    if (!isAllowedRole(newRole)) {
      toast({
        title: t('ops.staff.roleNotAvailable'),
        description: t('ops.staff.roleNotAvailableDesc', { role: roleDefinitions[newRole]?.title || newRole }),
        variant: 'destructive',
      });
      return;
    }
    try {
      await updateStaffRole(staffId, newRole);
      toast({
        title: t('ops.staff.roleUpdated'),
        description: t('ops.staff.roleUpdatedDesc', { role: roleDefinitions[newRole]?.title || newRole }),
      });
    } catch {
      toast({
        title: t('ops.staff.roleUpdateFailed'),
        description: t('ops.staff.roleUpdateFailedDesc'),
        variant: "destructive",
      });
    }
  };

  const handleRemoveStaff = (staffId, staffName) => {
    const staff = businessStaff.find(s => s.id === staffId);
    setStaffToRemove(staff);
    setRemoveDialogOpen(true);
  };

  const confirmRemoveStaff = async () => {
    if (!staffToRemove) return;
    try {
      await removeStaffMember(staffToRemove.id);
      toast({
        title: t('ops.staff.accessRevoked'),
        description: t('ops.staff.accessRevokedDesc', { name: staffToRemove.name }),
      });
    } catch {
      toast({
        title: t('ops.staff.removalFailed'),
        description: t('ops.staff.removalFailedDesc'),
        variant: "destructive",
      });
    } finally {
      setRemoveDialogOpen(false);
      setStaffToRemove(null);
    }
  };

  const businessStaff = getBusinessStaff();

  // Compute current usage per role (local roster — used only for the roster list, never as the
  // authoritative headline count).
  const usageByRole = (businessStaff || []).reduce((acc, m) => {
    acc[m.role] = (acc[m.role] || 0) + 1;
    return acc;
  }, {});

  // CANONICAL server-derived staff truth: tenant resolved from the token (no client brandId), so the
  // count/allowance can't be a false zero or a client-selected tenant. On failure we render an honest
  // "Usage temporarily unavailable" — NOT the local roster presented as authoritative truth.
  const { makeAuthenticatedRequest, user } = useCRMAuth();
  // DR-011 access gate: only Admin/Manager/IT roles may open the invite/import workflow (server also 403s).
  // ADM-01: the role now comes from THIS tenant's membership roster, not from `user.role` (which is the
  // login-form selection) nor `currentBusiness.role` (which never existed — a dead fallback). Same
  // backend-mirroring canManageStaff gate; only the input changed. Fails closed on no/ambiguous match,
  // and recomputes on tenant switch because businessStaff/brandId change with the selected business.
  const tenantRole = currentTenantRole(
    { firebaseUid: user?.uid || auth.currentUser?.uid, email: user?.email || auth.currentUser?.email },
    businessStaff, currentBusiness?.brandId,
  );
  const canInviteStaff = canManageStaff(tenantRole);
  const [canonStaff, setCanonStaff] = useState(null);
  const [canonState, setCanonState] = useState('loading');   // loading | ok | unavailable
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await makeAuthenticatedRequest('/api/v1/billing/me/usage-summary');
        if (!alive) return;
        if (r && r.ok) { const d = await r.json(); setCanonStaff(d.staff || null); setCanonState('ok'); }
        else setCanonState('unavailable');
      } catch (_) { if (alive) setCanonState('unavailable'); }
    })();
    return () => { alive = false; };
  }, [makeAuthenticatedRequest]);
  const rb = canonStaff?.role_breakdown || null;
  const canonTotal = canonStaff?.active_total;
  const canonAllowance = canonStaff?.effective_allowance;    // 130 for FJON; role_caps null → no /2 /6
  // Roster-derived counts (the authenticated staff list this page already loads — real backend data,
  // NOT a mock). Used as the display when the richer canonical summary is momentarily unavailable, so
  // the count always WORKS. Canonical is preferred (adds allowance + dedup + role_caps).
  const rosterRole = (role) => (businessStaff || []).filter((s) => s.role === role).length;
  const staffCell = (canonV, rosterV) => {
    if (canonState === 'ok' && canonV != null) return String(canonV);
    if (canonState === 'loading' && canonStaff == null && !(businessStaff && businessStaff.length)) return '…';
    return String(rosterV != null ? rosterV : (canonV != null ? canonV : 0));
  };
  const totalCell = staffCell(canonTotal, businessStaff ? businessStaff.length : null);
  const adminCell = staffCell(rb ? (rb.admin || 0) : null, rosterRole('admin'));
  const operatorCell = staffCell(rb ? (rb.operator || 0) : null, rosterRole('operator'));
  const otherCell = staffCell(
    rb ? Object.entries(rb).filter(([r]) => !['admin', 'operator'].includes(r)).reduce((a, [, v]) => a + v, 0) : null,
    (businessStaff || []).filter((s) => !['admin', 'operator'].includes(s.role)).length);

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      {/* Tier seat banner */}
      <div data-testid="tier-seat-banner" className="mb-6 bg-white/70 backdrop-blur-sm border border-purple-100 rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm text-gray-600">{t('ops.staff.subscriptionPlan')}</div>
            <div className="font-semibold capitalize text-gray-900">
              {t('ops.staff.seatAllocation')}
              {canonState === 'ok' && canonAllowance != null && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  {totalCell} of {canonAllowance === -1 ? 'Unlimited' : canonAllowance.toLocaleString()} allowed
                </span>
              )}
            </div>
          </div>
          <Badge className="bg-purple-100 text-purple-800 capitalize">{tierId}</Badge>
        </div>
        {/* Effective role allocation. role_caps=null (grandfathered) → show COUNTS with no per-role
            cap (never the base 2/2/6). Prefer the canonical breakdown; when it is momentarily
            unavailable, fall back to the authenticated roster this page already loaded so the
            allocation still renders real counts (not a fake, not a false zero). */}
        {(() => {
          const rosterBreakdown = (businessStaff || []).reduce((acc, s) => {
            const r = s.role || 'unknown'; acc[r] = (acc[r] || 0) + 1; return acc;
          }, {});
          const breakdown = (canonState === 'ok' && rb) ? rb : rosterBreakdown;
          const keys = Object.keys(breakdown).sort();
          if (keys.length === 0) {
            return <div className="text-xs text-gray-400">{canonState === 'loading' ? 'Loading allocation…' : 'No staff roles to display'}</div>;
          }
          return (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
              {keys.map(role => (
                <div key={role} className="px-2 py-1 rounded bg-gray-50 border border-gray-100 flex items-center justify-between">
                  <span className="capitalize text-gray-700">{role}</span>
                  <span className="font-semibold text-gray-900">
                    {breakdown[role]}{canonState === 'ok' && canonStaff?.role_caps && canonStaff.role_caps[role] != null ? `/${canonStaff.role_caps[role]}` : ''}
                  </span>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('nav.staffManagement')}</h1>
            <p className="text-gray-600">{t('page.staff.subtitle', { business: currentBusiness?.businessName || '' })}</p>
          </div>
          <div className="flex space-x-3">
            {canInviteStaff && (
            <Dialog open={inviteDialogOpen} onOpenChange={(o) => { setInviteDialogOpen(o); if (!o) resetInviteDialog(); }}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <UserPlus className="h-4 w-4 mr-2" />
                  {t('common.inviteStaff')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{t('ops.staff.inviteStaffMembers')}</DialogTitle>
                  <DialogDescription>
                    {t('ops.staff.inviteDescPre')} (<strong>{currentBusiness?.brandId}</strong>) {t('ops.staff.inviteDescMid')} <a href="/staff-signup" className="underline">/staff-signup</a>. {t('ops.staff.inviteExpire')}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  {/* OPERATOR-FIRST notice — every invitee joins as Operator; role assigned later by an admin. */}
                  <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3">
                    <Shield className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-800 leading-relaxed">{t('ops.staff.operatorFirstNotice')}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium">{t('ops.staff.emailAddresses')}</label>
                    <textarea
                      placeholder={`${t('ops.staff.emailsPlaceholder')}\njohn.doe@company.com\njane.smith@company.com, bob.wilson@company.com`}
                      value={inviteData.emails}
                      onChange={(e) => handleBulkEmailChange(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px] font-mono text-sm"
                      rows={4}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('ops.staff.validEmailsDetected', { count: parsedInvites.length })}
                    </p>
                  </div>

                  {/* CSV import — role column is ignored (operator-first). Non-mutating server preview. */}
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      ref={csvInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      className="hidden"
                      onChange={(e) => handleCsvFile(e.target.files && e.target.files[0])}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={() => csvInputRef.current && csvInputRef.current.click()}>
                      <Upload className="h-4 w-4 mr-2" />
                      {t('ops.staff.importCsv')}
                    </Button>
                    {csvFileName && (
                      <span className="text-xs text-gray-600 truncate max-w-[220px]">{sanitizeCsvDisplay(csvFileName)}</span>
                    )}
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={handlePreviewInvites}
                      disabled={previewing || parsedInvites.length === 0}
                    >
                      {previewing ? t('ops.staff.previewing') : t('ops.staff.previewInvites')}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 -mt-2">{t('ops.staff.csvFormatHelp')}</p>
                  {csvRoleIgnored && (
                    <p className="text-xs text-amber-700">{t('ops.staff.csvRoleIgnoredNotice')}</p>
                  )}

                  {/* Server preview results — scrollable, row-level outcomes, no raw ids/tokens. */}
                  {Array.isArray(previewRows) && (
                    <div>
                      <label className="text-sm font-medium">{t('ops.staff.previewResults', { count: previewRows.length })}</label>
                      <div className="mt-2 max-h-60 overflow-auto border border-gray-200 rounded-md">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-gray-50">
                            <tr className="text-left text-xs text-gray-500">
                              <th className="px-3 py-2">#</th>
                              <th className="px-3 py-2">{t('ops.staff.colName')}</th>
                              <th className="px-3 py-2">{t('ops.staff.colEmail')}</th>
                              <th className="px-3 py-2">{t('ops.staff.colJoinsAs')}</th>
                              <th className="px-3 py-2">{t('ops.staff.colOutcome')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {previewRows.map((r) => {
                              const meta = previewOutcomeMeta(r.outcome);
                              return (
                                <tr key={r.row} className="border-t border-gray-100">
                                  <td className="px-3 py-2 text-gray-400">{r.row}</td>
                                  <td className="px-3 py-2">{sanitizeCsvDisplay(r.display)}</td>
                                  <td className="px-3 py-2 text-gray-600">{sanitizeCsvDisplay(r.email)}</td>
                                  <td className="px-3 py-2">
                                    <Badge className="bg-blue-100 text-blue-800">{t('role.operator')}</Badge>
                                  </td>
                                  <td className="px-3 py-2"><Badge className={meta.cls}>{meta.label}</Badge></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{t('ops.staff.previewNonMutating')}</p>
                    </div>
                  )}

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => { setInviteDialogOpen(false); resetInviteDialog(); }}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      onClick={handleInviteStaff}
                      disabled={parsedInvites.length === 0 || inviteSending}
                      className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      {inviteSending ? t('ops.staff.sendingInvitations') : t('ops.staff.sendInvitations', { count: parsedInvites.length })}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        {/* Business Info Banner */}
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-blue-900">{currentBusiness?.businessName}</h3>
                <p className="text-blue-700 text-sm">{t('ops.staff.brandId')} {currentBusiness?.brandId}</p>
                <p className="text-blue-600 text-sm">{currentBusiness?.sector}</p>
              </div>
              <Badge className="bg-blue-100 text-blue-800">
                {t('ops.staff.teamMembersCount', { count: businessStaff.length })}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff Statistics */}
      <div className="grid md:grid-cols-4 gap-6 mb-8">
        <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('ops.staff.totalStaff')}</p>
                <p className="text-2xl font-bold text-gray-900">{totalCell}{canonState==='ok' && canonAllowance != null && <span className="text-base font-normal text-gray-500"> of {canonAllowance === -1 ? 'Unlimited' : canonAllowance.toLocaleString()}</span>}</p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('ops.staff.administrators')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {adminCell}
                </p>
              </div>
              <Shield className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('ops.staff.operators')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {operatorCell}
                </p>
              </div>
              <UserCheck className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{t('ops.staff.otherRoles')}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {otherCell}
                </p>
              </div>
              <Users className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff List */}
      <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('ops.staff.teamMembers')}</span>
            <Badge variant="secondary">{t('ops.staff.membersCount', { count: businessStaff.length })}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {businessStaff.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('ops.staff.noTeamMembers')}</h3>
              <p className="text-gray-500 mb-6">{t('ops.staff.noTeamMembersHelp')}</p>
              <Button
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                onClick={() => setInviteDialogOpen(true)}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {t('ops.staff.inviteFirst')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {businessStaff.map((staff) => (
                <div key={staff.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-gradient-to-r from-blue-400 to-purple-500 text-white font-semibold">
                          {staff.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div>
                        <h4 className="font-semibold text-gray-900">{staff.name}</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <Mail className="h-4 w-4" />
                            <span>{staff.email}</span>
                          </div>
                          {staff.phone && (
                            <div className="flex items-center space-x-1">
                              <Phone className="h-4 w-4" />
                              <span>{staff.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <Badge className={roleDefinitions[staff.role]?.color || 'bg-gray-100 text-gray-800'}>
                        {roleDefinitions[staff.role]?.title || staff.role}
                      </Badge>
                      
                      <div className="flex space-x-2">
                        <Select
                          value={staff.role}
                          onValueChange={(newRole) => handleRoleUpdate(staff.id, newRole)}
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(roleDefinitions)
                              .filter(([roleKey]) => tier.allowed.includes(roleKey))
                              .map(([roleKey, roleInfo]) => (
                              <SelectItem key={roleKey} value={roleKey}>
                                {roleInfo.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        <div className="relative" ref={actionMenuOpen && actionMenuStaff?.id === staff.id ? actionMenuRef : null}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => openActionMenu(staff, e)}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                          {actionMenuOpen && actionMenuStaff?.id === staff.id && (
                            <div className="absolute right-0 top-8 z-50 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[160px]">
                              <button
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-t-lg"
                                onClick={() => {
                                  setActionMenuOpen(false);
                                  setActionMenuStaff(null);
                                  // M24 — open the real role-change dialog (same handleRoleUpdate workflow as the dropdown)
                                  setRoleDialogStaff(staff);
                                  setRoleDialogValue(staff.role);
                                }}
                              >
                                <Edit className="h-4 w-4 text-blue-500" />
                                {t('ops.staff.changeRole')}
                              </button>
                              <button
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 rounded-b-lg border-t border-gray-100"
                                onClick={() => {
                                  setActionMenuOpen(false);
                                  setActionMenuStaff(null);
                                  handleRemoveStaff(staff.id, staff.name);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                                {t('ops.staff.removeStaff')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-3 text-sm text-gray-600">
                    {roleDefinitions[staff.role]?.description}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Staff Registration Instructions */}
      <Card className="mt-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
        <CardHeader>
          <CardTitle className="text-yellow-900">{t('ops.staff.regInstructions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-yellow-800">
            <p><strong>{t('ops.staff.forNewStaff')}</strong></p>
            <ol className="list-decimal list-inside space-y-1 ml-4">
              <li>{t('ops.staff.shareBrandId')} <strong className="font-mono text-lg">{currentBusiness?.brandId}</strong></li>
              <li>{t('ops.staff.directToReg')}</li>
              <li>{t('ops.staff.assignedOperatorDefault')}</li>
              <li>{t('ops.staff.updateRoleDropdown')}</li>
            </ol>
            <div className="mt-4 p-3 bg-yellow-100 rounded border border-yellow-300">
              <p className="font-medium">{t('ops.staff.registrationLink')}{' '}
                <a href="/staff-signup" className="font-mono text-blue-600 underline hover:text-blue-800" target="_blank" rel="noopener noreferrer">
                  /staff-signup
                </a>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Staff Removal Dialog */}
      {/* M24 — Change Role dialog: real role-change workflow (handleRoleUpdate -> updateStaffRole). */}
      <Dialog open={!!roleDialogStaff} onOpenChange={(o) => { if (!o) setRoleDialogStaff(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('ops.staff.changeRole')}</DialogTitle>
            <DialogDescription>
              {t('ops.staff.changeRoleDesc', { name: roleDialogStaff?.name || t('ops.staff.thisStaffMember') })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={roleDialogValue} onValueChange={setRoleDialogValue}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(roleDefinitions)
                  .filter(([roleKey]) => tier.allowed.includes(roleKey))
                  .map(([roleKey, roleInfo]) => (
                    <SelectItem key={roleKey} value={roleKey}>{roleInfo.title}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <div className="flex justify-end space-x-2 pt-2">
              <Button variant="outline" onClick={() => setRoleDialogStaff(null)} disabled={roleDialogSaving}>{t('common.cancel')}</Button>
              <Button
                disabled={roleDialogSaving || !roleDialogValue || roleDialogValue === roleDialogStaff?.role}
                onClick={async () => {
                  if (!roleDialogStaff) return;
                  setRoleDialogSaving(true);
                  try { await handleRoleUpdate(roleDialogStaff.id, roleDialogValue); setRoleDialogStaff(null); }
                  finally { setRoleDialogSaving(false); }
                }}
              >
                {roleDialogSaving ? t('ops.staff.saving') : t('ops.staff.saveRole')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-600">
              <AlertTriangle className="w-5 h-5 mr-2" />
              {t('ops.staff.removeStaffMember')}
            </DialogTitle>
            <DialogDescription>
              {t('ops.staff.removeWarning')}
            </DialogDescription>
          </DialogHeader>
          
          {staffToRemove && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-900 mb-2">{t('ops.staff.securityImpact')}</h4>
                <ul className="text-sm text-red-800 space-y-1">
                  <li>• <strong>{staffToRemove.name}</strong> {t('ops.staff.impact1')}</li>
                  <li>• {t('ops.staff.impact2a')} <strong>{t('ops.staff.impact2b')}</strong> {t('ops.staff.impact2c')}</li>
                  <li>• {t('ops.staff.impact3')}</li>
                  <li>• {t('ops.staff.impact4')}</li>
                </ul>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">{t('ops.staff.staffDetails')}</h4>
                <div className="text-sm text-blue-800 space-y-1">
                  <p><strong>{t('ops.common.nameLabel')}</strong> {staffToRemove.name}</p>
                  <p><strong>{t('ops.common.emailLabel')}</strong> {staffToRemove.email}</p>
                  <p><strong>{t('ops.staff.currentRole')}</strong> {staffToRemove.role}</p>
                  <p><strong>{t('ops.staff.joinDate')}</strong> {formatInTenantTz(staffToRemove.joinDate || staffToRemove.invitedDate, { dateStyle: 'medium' })}</p>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>{t('ops.staff.noteLabel')}</strong> {t('ops.staff.removeNote')}
                </p>
              </div>
              
              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setRemoveDialogOpen(false);
                    setStaffToRemove(null);
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={confirmRemoveStaff}
                >
                  {t('ops.staff.removeRevoke')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffManagement;